/**
 * @fileoverview Hook to get current authenticated user
 *
 * This hook provides access to the current user's Convex ID,
 * which is used for scoping all database queries.
 *
 * @module hooks/useCurrentUser
 */

import { useQuery } from 'convex/react'
import { api } from 'convex/_generated/api'

/**
 * Get the current authenticated user
 *
 * Returns the user object from Convex, or undefined if loading/not authenticated.
 * Use this to get the userId for all Convex queries that require user scoping.
 *
 * @returns User object with _id, name, email, etc., or undefined if loading, or null if not authenticated
 *
 * @example
 * ```tsx
 * const user = useCurrentUser()
 * if (user === undefined) return <div>Loading...</div>
 * if (user === null) return <div>Please sign in</div>
 *
 * // Use user._id for queries
 * const items = useQuery(api.vocabulary.getAllActive, {
 *   userId: user._id
 * })
 * ```
 */
export function useCurrentUser() {
  // The query will return undefined while loading, null if not authenticated,
  // or the user object if authenticated and stored in Convex
  return useQuery(api.users.current)
}
